import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { StatCard, Section, Empty, Button } from "@/components/ui/kit";
import JourneyStepper from "@/components/JourneyStepper";
import { fmtINR, fmtDate } from "@/utils/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Sparkles, Receipt, ArrowRight } from "lucide-react";

export default function DonorDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/donor/dashboard").then(r => setD(r.data.data)).catch(e => setErr(e.response?.data?.message)); }, []);
  if (err) return <Empty title="Dashboard unavailable" note={err} testid="donor-dashboard-error" />;
  if (!d) return <div className="text-muted-foreground">Loading…</div>;

  const allocPct = d.lifetime > 0 ? Math.min(100, (Number(d.allocated) / Number(d.lifetime)) * 100) : 0;
  const utilPct  = d.lifetime > 0 ? Math.min(100, (Number(d.utilized) / Number(d.lifetime)) * 100) : 0;

  const journeySteps = [
    { id:"received",   label:"Received",    detail: fmtINR(d.lifetime),    state: Number(d.lifetime) > 0 ? "done" : "pending" },
    { id:"allocated",  label:"Allocated",   detail: fmtINR(d.allocated),   state: Number(d.allocated) > 0 ? "done" : Number(d.lifetime) > 0 ? "active" : "pending" },
    { id:"utilized",   label:"Utilized",    detail: fmtINR(d.utilized),    state: Number(d.utilized) > 0 ? "done" : Number(d.allocated) > 0 ? "active" : "pending" },
    { id:"beneficiary",label:"Beneficiaries", detail: `${d.beneficiariesImpacted || 0} reached`, state: (d.beneficiariesImpacted || 0) > 0 ? "done" : "pending" },
    { id:"impact",     label:"Impact",      detail: "Photos & reports", state: (d.beneficiariesImpacted || 0) > 0 ? "done" : "pending" },
  ];

  return (
    <div className="space-y-10" data-testid="donor-dashboard">
      <div className="relative overflow-hidden rounded-3xl border border-emerald-900/15 bg-[hsl(160,84%,17%)] text-white p-8 lg:p-10">
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: "radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "18px 18px"}} />
        <div className="relative">
          <div className="overline !text-white/70">Welcome back</div>
          <h1 className="mt-1 font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Hello, {user?.fullName?.split(" ")[0]}.</h1>
          <p className="mt-2 text-white/80 max-w-2xl">Your contributions have moved from your account to real people. Here's the story so far.</p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Lifetime" value={fmtINR(d.lifetime)} />
            <MiniStat label="This academic year" value={fmtINR(d.currentAcademicYear)} />
            <MiniStat label="Matched by employer" value={fmtINR(d.matched)} />
            <MiniStat label="Donations" value={d.count} />
          </div>
        </div>
      </div>

      <Section title="Your contribution journey" kicker="Received → Allocated → Utilized → Beneficiary → Impact">
        <JourneyStepper steps={journeySteps} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Bar2 label="Allocated" value={fmtINR(d.allocated)} pct={allocPct} color="emerald" />
          <Bar2 label="Utilized" value={fmtINR(d.utilized)} pct={utilPct} color="orange" />
          <div className="rounded-xl border border-emerald-900/10 bg-white p-5">
            <div className="overline">Reach</div>
            <div className="mt-2 font-display text-3xl font-extrabold text-emerald-900">{d.beneficiariesImpacted || 0}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              beneficiaries touched • {d.projectsSupported || 0} projects • {d.activitiesSupported || 0} activities
            </div>
          </div>
        </div>
      </Section>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-emerald-900/10 bg-white p-6">
          <div className="overline">By project</div>
          <div className="h-64 mt-3">
            {d.byProject?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.byProject}>
                  <CartesianGrid stroke="#EDE7DA" strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v)=>fmtINR(v)} />
                  <Bar dataKey="amount" fill="#059669" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty title="No project contributions yet" />}
          </div>
        </div>
        <div className="rounded-xl border border-emerald-900/10 bg-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline">Recent</div>
              <div className="font-display text-lg font-bold text-emerald-950">Latest donations</div>
            </div>
            <Link to="/donor/contributions" data-testid="see-all-contributions" className="text-xs font-semibold text-emerald-800 hover:underline inline-flex items-center gap-1">See all <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="mt-3 divide-y divide-emerald-900/5">
            {d.recentDonations?.map(r => (
              <Link key={r.donationId} to={`/donor/contributions/${r.donationId}`} data-testid={`recent-donation-${r.donationId}`} className="py-3 flex items-start justify-between gap-3 hover:bg-emerald-50/40 -mx-2 px-2 rounded">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-emerald-950 truncate">{r.purpose}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(r.date)} • {r.receipt}</div>
                </div>
                <div className="mono font-semibold text-emerald-900 whitespace-nowrap">{fmtINR(r.amount)}</div>
              </Link>
            ))}
            {!d.recentDonations?.length && <div className="py-6 text-xs text-muted-foreground">No donations yet.</div>}
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-orange-700/20 bg-[#FDF6EE] p-8 relative overflow-hidden">
        <div className="relative flex items-start justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <div className="overline !text-orange-700">Stories from the field</div>
            <div className="mt-1 font-serif-display italic text-3xl text-emerald-950 leading-snug">
              "Your rupees became a scholarship, a lab, a bag."
            </div>
            <p className="mt-3 text-sm text-emerald-950/70">See photos, videos, and reports from the projects your contributions have touched.</p>
          </div>
          <Link to="/donor/impact"><Button variant="warm" data-testid="cta-my-impact"><Sparkles className="w-4 h-4"/> Explore my impact</Button></Link>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/10 backdrop-blur border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-white/70">{label}</div>
      <div className="mt-0.5 font-display text-lg font-extrabold">{value}</div>
    </div>
  );
}

function Bar2({ label, value, pct, color }) {
  const bg = color === "emerald" ? "bg-emerald-600" : "bg-orange-500";
  return (
    <div className="rounded-xl border border-emerald-900/10 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <div className="mono text-xs text-muted-foreground">{pct.toFixed(0)}%</div>
      </div>
      <div className="mt-2 font-display text-2xl font-extrabold text-emerald-900">{value}</div>
      <div className="mt-3 h-2 rounded-full bg-emerald-900/5 overflow-hidden">
        <div className={`h-full ${bg} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
